import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

export interface SupportTicket {
  id: string;
  customer_id: string;
  ticket_number: string;
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  assigned_to?: string;
  category?: string;
  created_at: string;
  updated_at: string;
  assigned_first_name?: string;
  assigned_last_name?: string;
}

export interface SupportMessage {
  id: string;
  ticket_id: string;
  user_id: string;
  message: string;
  is_internal: boolean;
  attachments?: any;
  created_at: string;
  author_name?: string;
  is_staff?: boolean;
  first_name?: string;
  last_name?: string;
  role?: string;
}

export interface CreateTicketData {
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category?: string;
}

export interface TicketStats {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
  closed: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class SupportService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  }

  async createTicket(ticketData: CreateTicketData): Promise<ApiResponse<SupportTicket>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/support/tickets`,
        ticketData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Support Service - Create Ticket Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create support ticket'
      };
    }
  }

  async getTickets(status?: string): Promise<ApiResponse<SupportTicket[]>> {
    try {
      const params = status && status !== 'all' ? { status } : {};
      const response = await axios.get(
        `${API_BASE_URL}/api/support/tickets`,
        {
          ...this.getAuthHeaders(),
          params
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Support Service - Get Tickets Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch support tickets'
      };
    }
  }

  async getTicket(ticketId: string): Promise<ApiResponse<SupportTicket>> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/support/tickets/${ticketId}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Support Service - Get Ticket Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch support ticket'
      };
    }
  }

  async getTicketWithMessages(ticketId: string): Promise<ApiResponse<{
    ticket: SupportTicket;
    messages: SupportMessage[];
  }>> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/support/tickets/${ticketId}/details`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Support Service - Get Ticket With Messages Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch ticket details'
      };
    }
  }

  async getTicketMessages(ticketId: string): Promise<ApiResponse<SupportMessage[]>> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/support/tickets/${ticketId}/messages`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Support Service - Get Messages Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch ticket messages'
      };
    }
  }

  async addMessage(ticketId: string, message: string): Promise<ApiResponse<SupportMessage>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/support/tickets/${ticketId}/messages`,
        { message },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Support Service - Add Message Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to add message'
      };
    }
  }

  async updateTicketStatus(ticketId: string, status: string): Promise<ApiResponse<SupportTicket>> {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/support/tickets/${ticketId}/status`,
        { status },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Support Service - Update Status Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update ticket status'
      };
    }
  }

  async getTicketStats(): Promise<ApiResponse<TicketStats>> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/support/tickets/stats`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      console.error('Support Service - Get Stats Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch ticket statistics'
      };
    }
  }

  // Close a ticket (customer action)
  async closeTicket(ticketId: string): Promise<ApiResponse<SupportTicket>> {
    return this.updateTicketStatus(ticketId, 'closed');
  }
}

export const supportService = new SupportService();